/**
 * The project gallery: photos taken in camera mode and the renders made from them —
 * Lite's `RenderGallery` (JPEG files + an index) done with IndexedDB. Photos are kept as
 * blobs in this browser; renders point at the server's files. A backend is injected so
 * the logic runs in tests without a browser.
 */
import type { RenderType } from './aiRender';
import { t } from '../i18n/I18n';

export interface GalleryItem {
  id: string;
  projectId: string;
  kind: 'photo' | 'render';
  createdAt: number;
  width?: number;
  height?: number;
  /** A photo's own bytes. */
  blob?: Blob;
  /** A render's server copy. */
  imageUrl?: string;
  thumbUrl?: string;
  initImageUrl?: string;
  /** The render project's id on the server. */
  renderId?: string;
  /** The photo the render was made from. */
  sourceId?: string;
  status?: 'running' | 'done' | 'error';
  error?: string;
  type?: RenderType;
  style?: string;
  prompt?: string;
}

export interface GalleryBackend {
  all(): Promise<GalleryItem[]>;
  put(item: GalleryItem): Promise<void>;
  delete(id: string): Promise<void>;
}

export class MemoryGallery implements GalleryBackend {
  private items = new Map<string, GalleryItem>();
  async all() { return [...this.items.values()]; }
  async put(item: GalleryItem) { this.items.set(item.id, { ...item }); }
  async delete(id: string) { this.items.delete(id); }
}

const DB = 'hp-gallery', STORE = 'items';

export class IndexedDbGallery implements GalleryBackend {
  private db: Promise<IDBDatabase>;
  constructor() {
    this.db = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB, 1);
      request.onupgradeneeded = () => { const db = request.result; if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' }).createIndex('projectId', 'projectId'); };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB unavailable'));
    });
  }
  private async run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = await this.db;
    return new Promise((resolve, reject) => {
      const request = work(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
    });
  }
  all() { return this.run<GalleryItem[]>('readonly', (store) => store.getAll()); }
  async put(item: GalleryItem) { await this.run('readwrite', (store) => store.put(item)); }
  async delete(id: string) { await this.run('readwrite', (store) => store.delete(id)); }
}

type Listener = () => void;

export class Gallery {
  private items = new Map<string, GalleryItem>();
  private listeners = new Set<Listener>();
  private urls = new Map<string, string>();
  private loaded: Promise<void> | null = null;
  loadError: string | null = null;

  constructor(private readonly backend: GalleryBackend, private readonly legacy: Pick<Storage, 'key' | 'length' | 'getItem' | 'removeItem'> | null = null) {}

  /** Read the store once; older renders kept in localStorage are carried over. */
  load(): Promise<void> {
    this.loaded ??= (async () => {
      for (const item of await this.backend.all()) this.items.set(item.id, item);
      await this.migrateLegacy();
      this.emit();
    })().catch((error) => { this.loadError = t('#LibraryGalleryReadFailed'); this.emit(); console.warn('Gallery unavailable:', error); });
    return this.loaded;
  }

  private async migrateLegacy(): Promise<void> {
    if (!this.legacy) return;
    const keys: string[] = [];
    for (let i = 0; i < this.legacy.length; i++) { const key = this.legacy.key(i); if (key?.startsWith('hp.renders.')) keys.push(key); }
    for (const key of keys) {
      try {
        const records = JSON.parse(this.legacy.getItem(key) ?? '[]') as Array<{ id: string; projectId: string; type: RenderType; createdAt: number; status: 'running' | 'done' | 'error'; imageUrl?: string; thumbUrl?: string; initImageUrl?: string; width?: number; height?: number; prompt?: string; style?: string; error?: string }>;
        for (const r of records) {
          if (r.status !== 'done' || this.items.has(`render:${r.id}`)) continue;
          await this.backend.put({ id: `render:${r.id}`, projectId: r.projectId, kind: 'render', createdAt: r.createdAt, renderId: r.id, status: 'done', imageUrl: r.imageUrl, thumbUrl: r.thumbUrl, initImageUrl: r.initImageUrl, width: r.width, height: r.height, type: r.type, style: r.style, prompt: r.prompt });
          this.items.set(`render:${r.id}`, { id: `render:${r.id}`, projectId: r.projectId, kind: 'render', createdAt: r.createdAt, renderId: r.id, status: 'done', imageUrl: r.imageUrl, thumbUrl: r.thumbUrl, initImageUrl: r.initImageUrl, width: r.width, height: r.height, type: r.type, style: r.style, prompt: r.prompt });
        }
      } catch { /* a damaged legacy list is dropped */ }
      this.legacy.removeItem(key);
    }
  }

  all(): GalleryItem[] {
    return [...this.items.values()].sort((a, b) => b.createdAt - a.createdAt);
  }

  list(projectId: string): GalleryItem[] {
    return [...this.items.values()].filter((item) => item.projectId === projectId).sort((a, b) => b.createdAt - a.createdAt);
  }

  get(id: string): GalleryItem | undefined { return this.items.get(id); }

  async add(item: GalleryItem): Promise<GalleryItem> {
    this.items.set(item.id, item);
    this.emit();
    await this.backend.put(item).catch((error) => console.warn('Gallery: not saved', error));
    return item;
  }

  async update(id: string, patch: Partial<GalleryItem>): Promise<GalleryItem | undefined> {
    const current = this.items.get(id);
    if (!current) return undefined;
    const next = { ...current, ...patch };
    this.items.set(id, next);
    this.emit();
    await this.backend.put(next).catch((error) => console.warn('Gallery: not saved', error));
    return next;
  }

  async remove(id: string): Promise<void> {
    this.items.delete(id);
    const url = this.urls.get(id);
    if (url) { URL.revokeObjectURL(url); this.urls.delete(id); }
    this.emit();
    await this.backend.delete(id).catch((error) => console.warn('Gallery: not deleted', error));
  }

  /** An image URL for an item: a render's server copy, a photo's object URL (cached until removal). */
  imageUrl(item: GalleryItem): string | undefined {
    if (item.kind === 'render') return item.imageUrl;
    if (!item.blob) return undefined;
    let url = this.urls.get(item.id);
    if (!url) { url = URL.createObjectURL(item.blob); this.urls.set(item.id, url); }
    return url;
  }

  thumbUrl(item: GalleryItem): string | undefined {
    return item.kind === 'render' ? (item.thumbUrl ?? item.imageUrl) : this.imageUrl(item);
  }

  subscribe(listener: Listener): () => void { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; }
  private emit(): void { for (const listener of this.listeners) listener(); }
}

export const newGalleryId = (kind: GalleryItem['kind']): string =>
  `${kind}:${typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2, 8)}`;

const backend: GalleryBackend = typeof indexedDB === 'undefined' ? new MemoryGallery() : new IndexedDbGallery();
export const gallery = new Gallery(backend, typeof localStorage === 'undefined' ? null : localStorage);
if (typeof window !== 'undefined' && import.meta.env?.DEV) {
  (window as unknown as { hpGallery?: Gallery }).hpGallery = gallery;
}
